import { ChipList } from "@components/layout/ChipList"
import { FileItem } from "@components/media/FileItem"
import { createTRPCClient, httpLink } from "@trpc/client"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { useRPCMutation, useRPCQuery } from "eddev/hooks"
import { useRestorableState } from "eddev/routing"
import { motion, useMotionValue } from "framer-motion"
import { MutableRefObject, useEffect, useRef, useState } from "react"
import { ResourceCardFragment } from "../../types.graphql"
import { Button } from "@components/controls/Button"

export const meta: BlockMeta = {
  title: "Featured Resources",
  inserter: false,
  flags: {
    fullWidth: true,
    selfAnimates: true,
  },
}

export default defineBlock("resources/featured-resources", (props) => {
  return (
    <>
      <FeaturedResourcesDesktop {...props} />
      <FeaturedResourcesMobile {...props} />
      <MaybeRPCDemo />
    </>
  )
})

function MaybeRPCDemo() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (document.location.href.includes("rpc")) {
      setShow(true)
    }
  }, [])

  if (show) {
    return <RPCDemo />
  } else {
    return null
  }
}

function RPCDemo() {
  const result = useRPCQuery("api.doSomething", {
    name: "Dan",
  })

  const mutation = useRPCMutation("mutation-test")

  return (
    <div>
      <div className="border border-error-dark">
        <div className="p-2">Result: {JSON.stringify(result.data)}</div>
        <div className="p-2">Error: {JSON.stringify(result.error)}</div>
        <div className="p-2">Loading: {result.status}</div>
      </div>
      <div className="border border-error-dark">
        <div className="p-2">Result: {JSON.stringify(mutation.data)}</div>
        <div className="p-2">Error: {JSON.stringify(mutation.error)}</div>
        <div className="p-2">Loading: {mutation.status}</div>
        <Button
          onClick={() => {
            mutation.mutate({
              name: "Dan",
            })
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

function FeaturedResourcesDesktop(props: BlockProps["resources/featured-resources"]) {
  const outer = useRef<HTMLDivElement>(null!)

  return (
    <div ref={outer} className="w-full md:h-[80vh] md:max-h-[40vw] relative hidden md:block">
      {props.resources?.map((resource, index) => {
        return <Item {...resource!} index={index} id={resource?.databaseId!} key={resource?.databaseId} outerRef={outer} />
      })}
    </div>
  )
}

function FeaturedResourcesMobile(props: BlockProps["resources/featured-resources"]) {
  return (
    <div className="w-full pt-1 pb-6 relative flex md:hidden overflow-auto gap-[30px] scrollbar-hidden">
      {props.resources?.map((resource, index) => {
        return (
          <div key={resource?.databaseId} className="w-[200px] flex-none">
            <FileItem
              href={resource?.uri!}
              icon={resource?.typeIcon ?? "file"}
              title={<EditableText as="div" store={"label_" + resource?.databaseId} defaultValue={resource?.title ?? "Untitled"} placeholder={resource?.title ?? "Untitled"} />}
              chips={<ChipList className="justify-center" chips={resource?.chips!} />}
            />
          </div>
        )
      })}
    </div>
  )
}

function useStoredCoords(id: number) {
  const store = useInlineEditableValue<[number, number]>("p" + id, [0.5, 0.5])

  if (env.admin) {
    return store
  } else {
    // Store the position in the route, so going forward/back keeps items in the same spot
    return useRestorableState("temp" + id, store[0])
  }
}

function Item(props: ResourceCardFragment & { id: number; outerRef: MutableRefObject<HTMLDivElement>; index: number }) {
  const ref = useRef<HTMLDivElement>(null!)
  const [pos, setPos] = useStoredCoords(props.id)

  const [ready, setReady] = useState(false)

  const posX = useMotionValue(0)
  const posY = useMotionValue(0)

  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    setReady(true)
    if (!dragging) {
      const update = () => {
        const bounds = props.outerRef.current?.getBoundingClientRect()
        if (!bounds) return
        posX.set(pos[0] * bounds.width)
        posY.set(pos[1] * bounds.height)
      }
      update()
      const ro = new ResizeObserver(update)
      ro.observe(props.outerRef.current)
      return () => ro.disconnect()
    }
  }, [dragging, pos])

  if (!ready) return null

  return (
    <motion.div
      ref={ref}
      key={props.uri}
      className="md:w-[20%] max-w-[280px] md:absolute transform-gpu"
      style={{ x: posX, y: posY }}
      _dragX={posX}
      _dragY={posY}
      draggable
      drag
      dragConstraints={dragging ? props.outerRef : undefined}
      dragMomentum={false}
      dragTransition={{
        bounceStiffness: 500,
        bounceDamping: 18,
      }}
      onDragStart={() => {
        setDragging(true)
      }}
      onDragTransitionEnd={() => {
        const bounds = props.outerRef.current.getBoundingClientRect()
        setPos([posX.get() / bounds.width, posY.get() / bounds.height])
        setDragging(false)
      }}
      onClickCapture={(e) => {
        if (dragging) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.1, delay: props.index * 0.05 }}
    >
      <FileItem
        href={props.uri!}
        icon={props.typeIcon ?? "file"}
        title={<EditableText as="div" store={"label_" + props.id} defaultValue={props.title ?? "Untitled"} placeholder={props.title ?? "Untitled"} />}
        chips={<ChipList className="justify-center" chips={props.chips!} />}
      />
    </motion.div>
  )
}
